import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import sanityClient from "../../sanityClient";
import { Link } from "react-router-dom";

const POSTS_PER_PAGE = 8;

const LatestNews = () => {
  const [pressReleases, setPressReleases] = useState([]);
  const [filteredReleases, setFilteredReleases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "pressRelease"] | order(date desc) {
          _id,
          title,
          body,
          date,
          "pdfUrl": mainImage.asset->url
        }`
      )
      .then((data) => {
        setPressReleases(data);
        setFilteredReleases(data);
        setTotalPages(Math.ceil(data.length / POSTS_PER_PAGE));
      })
      .catch(console.error);
  }, []);

  const getTruncatedText = (body) => {
    const plainText = body
      .map((block) => block.children.map((child) => child.text).join(" "))
      .join(" ");

    const truncatedText = plainText.split(" ").slice(0, 30).join(" ");
    return (
      truncatedText + (plainText.length > truncatedText.length ? "..." : "")
    );
  };

  const paginateReleases = () => {
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    const endIndex = startIndex + POSTS_PER_PAGE;
    return filteredReleases.slice(startIndex, endIndex);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    const filtered = pressReleases.filter(
      (release) =>
        release.title.toLowerCase().includes(term) ||
        new Date(release.date).toLocaleDateString().includes(term) ||
        release.body
          .map((block) => block.children.map((child) => child.text).join(" "))
          .join(" ")
          .toLowerCase()
          .includes(term)
    );

    setFilteredReleases(filtered);
    setTotalPages(Math.ceil(filtered.length / POSTS_PER_PAGE));
    setCurrentPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Latest News - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Stay updated with the latest news and press releases from Habitat for Humanity Buffalo. Learn about our recent projects, community impact, and more."
        />
      </Helmet>
      <Container>
        <Header>Habitat Buffalo in the News</Header>

        <SearchBar>
          <SearchInput
            type="text"
            placeholder="Search by title, date, or description..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </SearchBar>

        {filteredReleases.length > 0 ? (
          <ReleaseGrid>
            {paginateReleases().map((release) => (
              <Release key={release._id}>
                <ReleaseContent>
                  <Title>{release.title}</Title>
                  <DateText>
                    {new Date(release.date).toLocaleDateString()}
                  </DateText>
                  <Body>{getTruncatedText(release.body)}</Body>
                  <Button to={`/newsroom/${release._id}`}>Read More</Button>
                </ReleaseContent>
              </Release>
            ))}
          </ReleaseGrid>
        ) : (
          <NoReleases>No press releases available.</NoReleases>
        )}

        <Pagination>
          <PaginationButton
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            Previous
          </PaginationButton>
          {Array.from({ length: totalPages }, (_, i) => (
            <PageNumber
              key={i + 1}
              onClick={() => handlePageClick(i + 1)}
              isActive={currentPage === i + 1}
            >
              {i + 1}
            </PageNumber>
          ))}
          <PaginationButton
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </PaginationButton>
        </Pagination>
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--container-padding);
  background-color: var(--background-color);

  border-radius: 8px;
`;

const Header = styled.h1`
  text-align: center;
  font-size: 1.8em;
  color: var(--primary-color);
  margin-bottom: 30px;
`;

const SearchBar = styled.div`
  margin-bottom: 20px;
  text-align: center;
`;

const SearchInput = styled.input`
  padding: 10px;
  font-size: 1em;
  width: 100%;
  max-width: 600px;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const ReleaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const Release = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: var(--secondary-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }
`;

const ReleaseContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Title = styled.h2`
  font-size: 1.2em;
  color: var(--primary-color);
  text-align: left;
  margin: 0;
`;

const DateText = styled.p`
  font-size: 0.9em;
  color: #999;
  text-align: left;
  margin: 0;
`;

const Body = styled.p`
  font-size: 1em;
  line-height: 1.4;
  overflow: hidden;
  margin: 0;
  text-align: left;
`;

const Button = styled(Link)`
  display: inline-block;
  align-self: flex-start;
  background-color: var(--primary-color);
  color: var(--secondary-color);
  padding: 10px 15px;
  text-decoration: none;
  border-radius: 5px;
  font-size: 0.9em;
  font-weight: bold;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--accent-color);
  }
`;

const NoReleases = styled.p`
  text-align: center;
  font-size: 1em;
  color: #666;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

const PaginationButton = styled.button`
  background: var(--primary-color);
  color: #fff;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PageNumber = styled.span`
  cursor: pointer;
  padding: 5px 10px;
  font-size: 0.9em;
  font-weight: bold;
  color: ${({ isActive }) => (isActive ? "#fff" : "#000")};
  background-color: ${({ isActive }) =>
    isActive ? "var(--primary-color)" : "transparent"};
  border: ${({ isActive }) => (isActive ? "none" : "1px solid #ddd")};
  border-radius: 5px;
  transition: all 0.3s;

  &:hover {
    background-color: var(--primary-color);
    color: #fff;
  }
`;

export default LatestNews;
